import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { http } from "../lib/axios";
import * as agreementsApi from "../lib/apiHostingAgreements";
import {
  groupAgreementsByStatus,
  mergeAgreementUpdate,
  normalizeAgreement,
  normalizeAgreementList,
  removeAgreementById,
  removeHostedArtworkByArtworkId,
} from "../lib/hostingAgreementViewModel";

function getApiFn(name) {
  return agreementsApi?.[name];
}

async function getCurrentHostProfile() {
  const meRes = await http.get("/api/accounts/me/");
  const userId = meRes?.data?.id;
  if (!userId) return null;

  const hostRes = await http.get(`/hosts/?user=${userId}`);
  const host = Array.isArray(hostRes?.data) ? hostRes.data[0] : null;
  return host || null;
}

async function listHostAgreementsFallback() {
  const host = await getCurrentHostProfile();
  const response = await agreementsApi.listMyArtworkAgreements();
  const normalized = normalizeAgreementList(response).agreements;

  if (!host?.id) {
    return {
      agreements: normalized,
      host,
      raw: response,
    };
  }

  const agreements = normalized.filter((item) => Number(item?.host?.id) === Number(host.id));
  return {
    agreements,
    host,
    raw: response,
  };
}

async function listHostAgreements() {
  const listHostArtworkAgreements = getApiFn("listHostArtworkAgreements");
  if (typeof listHostArtworkAgreements === "function") {
    return listHostArtworkAgreements();
  }
  return listHostAgreementsFallback();
}

async function fetchHostedArtworks() {
  const response = await http.get("/hosts/hosted_artworks/", { params: { view: "card" } });
  return Array.isArray(response?.data) ? response.data : [];
}

function extractAgreementFromPayload(payload) {
  return payload?.agreement || payload?.data?.agreement || payload?.data || payload;
}

function getRelatedUpdates(payload) {
  return payload?.related_updates || payload?.data?.related_updates || {};
}

export function useHostHostedArtworks({
  enabled = true,
  loadHostedArtworks = true,
  onError,
} = {}) {
  const [agreements, setAgreementsState] = useState([]);
  const [hostedArtworks, setHostedArtworks] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  const groupedAgreements = useMemo(
    () => groupAgreementsByStatus(agreements),
    [agreements]
  );

  const requestedArtworkIds = useMemo(
    () => agreements
      .filter((item) => item?.statusGroup === "pending")
      .map((item) => item?.artwork?.id)
      .filter(Boolean),
    [agreements]
  );

  const setAgreements = (updater) => {
    setAgreementsState((prev) => (
      typeof updater === "function" ? updater(prev) : updater
    ));
  };

  const applyAgreementUpdate = (updatedAgreement) => {
    setAgreementsState((prev) => mergeAgreementUpdate(prev, updatedAgreement));
  };

  const applyMutationPayload = (payload) => {
    const agreement = extractAgreementFromPayload(payload);
    const relatedUpdates = getRelatedUpdates(payload);

    if (agreement?.id) {
      applyAgreementUpdate(agreement);
    }

    if (relatedUpdates?.original_agreement?.id) {
      applyAgreementUpdate(relatedUpdates.original_agreement);
    }

    if (relatedUpdates?.replacement_agreement?.id) {
      applyAgreementUpdate(relatedUpdates.replacement_agreement);
    }

    if (relatedUpdates?.artwork?.id) {
      setHostedArtworks((prev) => prev.map((item) => {
        const nestedArtworkId = item?.artwork?.id ?? item?.artwork_id ?? item?.artwork;
        if (Number(nestedArtworkId) !== Number(relatedUpdates.artwork.id)) return item;
        if (item?.artwork && typeof item.artwork === "object") {
          return {
            ...item,
            artwork: { ...item.artwork, ...relatedUpdates.artwork },
          };
        }
        return item;
      }));
    }

    if (relatedUpdates?.hosted_artwork_removed?.artwork_id) {
      setHostedArtworks((prev) => removeHostedArtworkByArtworkId(
        prev,
        relatedUpdates.hosted_artwork_removed.artwork_id
      ));
    }
  };

  const refreshHostedArtworks = useCallback(async () => {
    if (!enabled || !loadHostedArtworks) return [];
    const rows = await fetchHostedArtworks();
    setHostedArtworks(rows);
    return rows;
  }, [enabled, loadHostedArtworks]);

  const refreshAgreements = useCallback(async () => {
    if (!enabled) return [];

    const isInitialLoad = !hasLoadedRef.current;
    setError(null);
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const result = await listHostAgreements();
      const normalized = Array.isArray(result?.agreements)
        ? result.agreements.map((item) => normalizeAgreement(item?.raw || item))
        : normalizeAgreementList(result?.raw || result).agreements;

      setAgreementsState(normalized);
      hasLoadedRef.current = true;

      if (loadHostedArtworks) {
        await refreshHostedArtworks();
      }

      return normalized;
    } catch (err) {
      setError(err);
      if (typeof onError === "function") onError(err);
      throw err;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [enabled, loadHostedArtworks, onError, refreshHostedArtworks]);

  useEffect(() => {
    if (!enabled) return;
    refreshAgreements().catch(() => {});
  }, [enabled, loadHostedArtworks, refreshAgreements]);

  const openAgreementById = (agreementId) => (
    agreements.find((agreement) => Number(agreement?.id) === Number(agreementId)) || null
  );

  const createOffer = async (artworkId, terms, options = {}) => {
    const payload = await agreementsApi.createHostOffer(artworkId, terms, options);
    applyMutationPayload(payload);
    return payload;
  };

  const createReplacementOffer = async (artworkId, originalAgreementId, terms, options = {}) => {
    const payload = await agreementsApi.createReplacementHostOffer(
      artworkId,
      originalAgreementId,
      terms,
      options
    );
    applyMutationPayload(payload);
    return payload;
  };

  const acceptAgreement = async (agreementId) => {
    const payload = await agreementsApi.acceptArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const rejectAgreement = async (agreementId) => {
    const payload = await agreementsApi.rejectArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const counterofferAgreement = async (agreementId, patchTerms, options = {}) => {
    const mode = options?.mode || "respond";

    if (mode === "changeTerms") {
      return createReplacementOffer(
        options?.artworkId,
        options?.originalAgreementId || agreementId,
        patchTerms,
        { hostId: options?.hostId }
      );
    }

    const payload = await agreementsApi.counterofferArtworkAgreement(agreementId, patchTerms);
    applyMutationPayload(payload);
    return payload;
  };

  const requestAgreementCancellation = async (agreementId) => {
    const payload = await agreementsApi.requestCancelArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const confirmAgreementCancellation = async (agreementId) => {
    const payload = await agreementsApi.confirmCancelArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const rejectAgreementCancellation = async (agreementId) => {
    const payload = await agreementsApi.rejectCancelArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const markAgreementSold = async (agreementId, options = {}) => {
    let payload;
    const markArtworkAgreementSold = getApiFn("markArtworkAgreementSold");
    const markHostedAgreementSold = getApiFn("markHostedAgreementSold");

    if (typeof markArtworkAgreementSold === "function") {
      payload = await markArtworkAgreementSold(agreementId, options);
    } else {
      const agreement = openAgreementById(agreementId);
      const artworkId = options?.artworkId || agreement?.artwork?.id;
      if (!artworkId) {
        throw new Error("markAgreementSold requires an agreementId with an associated artwork.");
      }
      await http.patch(`/artworks/${artworkId}/mark_sold/`);
      if (typeof markHostedAgreementSold === "function") {
        await markHostedAgreementSold(artworkId);
      }
      payload = {
        agreement: {
          ...(agreement?.raw || agreement),
          id: agreementId,
          status: "SOLD",
        },
        related_updates: {
          artwork: {
            id: artworkId,
            status: "sold",
          },
        },
      };
    }

    applyMutationPayload(payload);
    return payload;
  };

  return {
    agreements,
    groupedAgreements,
    hostedArtworks,
    requestedArtworkIds,
    loading,
    refreshing,
    error,

    refreshAgreements,
    refreshHostedArtworks,
    setAgreements,

    openAgreementById,
    applyAgreementUpdate,
    applyMutationPayload,

    createOffer,
    createReplacementOffer,
    acceptAgreement,
    rejectAgreement,
    counterofferAgreement,
    requestAgreementCancellation,
    confirmAgreementCancellation,
    rejectAgreementCancellation,
    markAgreementSold,
  };
}

export function removeHostAgreement(agreements, agreementId) {
  return removeAgreementById(agreements, agreementId);
}
